import { useEffect, useRef, useCallback } from "react";

type WeatherType = "clear" | "partly-cloudy" | "cloudy" | "fog" | "drizzle" | "rain" | "heavy-rain" | "snow" | "thunderstorm";
type TimeOfDay = "night" | "dawn" | "day" | "dusk";

function getWeatherType(code: number): WeatherType {
  if (code <= 1) return "clear";
  if (code === 2) return "partly-cloudy";
  if (code === 3) return "cloudy";
  if (code === 45 || code === 48) return "fog";
  if (code >= 51 && code <= 55) return "drizzle";
  if ((code >= 61 && code <= 63) || (code >= 80 && code <= 81)) return "rain";
  if (code === 65 || code === 66 || code === 67 || code === 82) return "heavy-rain";
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "snow";
  if (code >= 95) return "thunderstorm";
  return "cloudy";
}

function getTimeOfDay(sunrise?: string, sunset?: string, timezone?: string): TimeOfDay {
  if (!sunrise || !sunset) return "day";
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || "UTC",
      hour: "numeric",
      minute: "numeric",
      hour12: false,
    });
    const parts = formatter.formatToParts(now);
    const hour = parseInt(parts.find(p => p.type === "hour")?.value || "12");
    const minute = parseInt(parts.find(p => p.type === "minute")?.value || "0");
    const nowMinutes = hour * 60 + minute;

    const sr = new Date(sunrise);
    const srMinutes = sr.getHours() * 60 + sr.getMinutes();
    const ss = new Date(sunset);
    const ssMinutes = ss.getHours() * 60 + ss.getMinutes();

    const dawnStart = srMinutes - 45;
    const dawnEnd = srMinutes + 30;
    const duskStart = ssMinutes - 30;
    const duskEnd = ssMinutes + 45;

    if (nowMinutes >= dawnStart && nowMinutes < dawnEnd) return "dawn";
    if (nowMinutes >= duskStart && nowMinutes < duskEnd) return "dusk";
    if (nowMinutes >= dawnEnd && nowMinutes < duskStart) return "day";
    return "night";
  } catch {
    return "day";
  }
}

interface Raindrop { x: number; y: number; speed: number; length: number; opacity: number; wind: number; }
interface Splash { x: number; y: number; radius: number; maxRadius: number; opacity: number; life: number; }
interface Snowflake { x: number; y: number; speed: number; radius: number; opacity: number; drift: number; wobble: number; wobbleSpeed: number; }
interface FogPatch { x: number; y: number; radius: number; opacity: number; speed: number; }
interface LightningFlash { opacity: number; timer: number; active: boolean; nextFlash: number; bolts: LightningBolt[]; }
interface LightningBolt { segments: { x: number; y: number }[]; opacity: number; width: number; branches: { x: number; y: number }[][]; }
interface CloudBlob { cx: number; cy: number; rx: number; ry: number; }
interface Cloud { x: number; y: number; speed: number; opacity: number; blobs: CloudBlob[]; width: number; }

function makeCloud(x: number, y: number, scale: number): Cloud {
  const blobCount = 5 + Math.floor(Math.random() * 4);
  const blobs: CloudBlob[] = [];
  const baseW = (180 + Math.random() * 200) * scale;
  const baseH = (35 + Math.random() * 25) * scale;

  // Base flat bottom
  blobs.push({ cx: 0, cy: 0, rx: baseW * 0.5, ry: baseH * 0.5 });

  // Bumpy top
  for (let i = 1; i < blobCount; i++) {
    const t = (i / (blobCount - 1)) * 2 - 1; // -1 to 1
    const bx = t * baseW * 0.38;
    const by = -(baseH * 0.2 + Math.random() * baseH * 0.7);
    const brx = baseW * (0.15 + Math.random() * 0.2);
    const bry = baseH * (0.4 + Math.random() * 0.5);
    blobs.push({ cx: bx, cy: by, rx: brx, ry: bry });
  }

  return {
    x, y, speed: 0.08 + Math.random() * 0.15,
    opacity: 0.06 + Math.random() * 0.06,
    blobs, width: baseW,
  };
}

// Gradient palettes per time-of-day + weather
type GradientDef = { top: string; mid: string; bot: string };

function getGradient(time: TimeOfDay, weather: WeatherType): GradientDef {
  // Base palettes for time of day
  const base: Record<TimeOfDay, GradientDef> = {
    night: { top: "#020617", mid: "#0f172a", bot: "#1e293b" },
    dawn:  { top: "#1e1b4b", mid: "#4c1d95", bot: "#f59e0b" },
    day:   { top: "#0c4a6e", mid: "#0369a1", bot: "#7dd3fc" },
    dusk:  { top: "#1e1b4b", mid: "#9333ea", bot: "#f97316" },
  };

  // Weather overrides darken/shift the palette
  const g = { ...base[time] };

  if (weather === "cloudy" || weather === "fog") {
    if (time === "day") { g.top = "#334155"; g.mid = "#475569"; g.bot = "#94a3b8"; }
    else if (time === "night") { g.top = "#0f172a"; g.mid = "#1e293b"; g.bot = "#334155"; }
    else if (time === "dawn") { g.top = "#1e1b4b"; g.mid = "#374151"; g.bot = "#d97706"; }
    else { g.top = "#1e1b4b"; g.mid = "#374151"; g.bot = "#c2410c"; }
  } else if (weather === "rain" || weather === "drizzle") {
    if (time === "day") { g.top = "#1e293b"; g.mid = "#334155"; g.bot = "#64748b"; }
    else if (time === "night") { g.top = "#020617"; g.mid = "#0f172a"; g.bot = "#1e293b"; }
    else { g.top = "#1e1b4b"; g.mid = "#334155"; g.bot = "#78716c"; }
  } else if (weather === "heavy-rain" || weather === "thunderstorm") {
    if (time === "day") { g.top = "#0f172a"; g.mid = "#1e293b"; g.bot = "#475569"; }
    else { g.top = "#020617"; g.mid = "#0a0f1a"; g.bot = "#1e293b"; }
  } else if (weather === "snow") {
    if (time === "day") { g.top = "#475569"; g.mid = "#94a3b8"; g.bot = "#cbd5e1"; }
    else if (time === "night") { g.top = "#0f172a"; g.mid = "#1e293b"; g.bot = "#475569"; }
    else { g.top = "#312e81"; g.mid = "#64748b"; g.bot = "#cbd5e1"; }
  } else if (weather === "partly-cloudy") {
    if (time === "day") { g.top = "#0c4a6e"; g.mid = "#0ea5e9"; g.bot = "#bae6fd"; }
    else if (time === "night") { g.top = "#020617"; g.mid = "#0f172a"; g.bot = "#1e3a5f"; }
  }
  return g;
}

function parseColor(c: string): [number, number, number] {
  if (c.startsWith("#")) {
    return [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
  }
  const m = c.match(/(\d+)/g);
  if (m && m.length >= 3) return [+m[0], +m[1], +m[2]];
  return [0, 0, 0];
}

function lerpColor(a: string, b: string, t: number): string {
  const pa = parseColor(a);
  const pb = parseColor(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `rgb(${r},${g},${bl})`;
}

export function WeatherBackground({ weatherCode, sunrise, sunset, timezone }: {
  weatherCode: number; sunrise?: string; sunset?: string; timezone?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const stateRef = useRef<{
    type: WeatherType;
    timeOfDay: TimeOfDay;
    raindrops: Raindrop[];
    splashes: Splash[];
    snowflakes: Snowflake[];
    fogPatches: FogPatch[];
    lightning: LightningFlash;
    clouds: Cloud[];
    cardRects: DOMRect[];
    time: number;
    gradient: GradientDef;
    targetGradient: GradientDef;
    currentGradient: GradientDef;
    stars: { x: number; y: number; r: number; twinkleSpeed: number; phase: number }[];
  }>({
    type: "clear",
    timeOfDay: "day",
    raindrops: [],
    splashes: [],
    snowflakes: [],
    fogPatches: [],
    lightning: { opacity: 0, timer: 0, active: false, nextFlash: 200 },
    clouds: [],
    cardRects: [],
    time: 0,
    gradient: { top: "#020617", mid: "#0f172a", bot: "#1e293b" },
    targetGradient: { top: "#020617", mid: "#0f172a", bot: "#1e293b" },
    currentGradient: { top: "#020617", mid: "#0f172a", bot: "#1e293b" },
    stars: [],
  });

  const getCardRects = useCallback(() => {
    const cards = document.querySelectorAll("[data-weather-surface]");
    return Array.from(cards).map((el) => el.getBoundingClientRect());
  }, []);

  const initParticles = useCallback((type: WeatherType, tod: TimeOfDay, w: number, h: number) => {
    const s = stateRef.current;
    s.type = type;
    s.timeOfDay = tod;
    s.targetGradient = getGradient(tod, type);

    // Rain
    if (type === "drizzle" || type === "rain" || type === "heavy-rain" || type === "thunderstorm") {
      const count = type === "drizzle" ? 60 : type === "rain" ? 150 : type === "heavy-rain" ? 250 : 450;
      s.raindrops = Array.from({ length: count }, () => ({
        x: Math.random() * (w + 200) - 100,
        y: Math.random() * h - h,
        speed: type === "thunderstorm" ? 14 + Math.random() * 12 : 8 + Math.random() * 8 + (type === "heavy-rain" ? 6 : 0),
        length: type === "drizzle" ? 8 + Math.random() * 8 : type === "thunderstorm" ? 20 + Math.random() * 20 : 14 + Math.random() * 14,
        opacity: type === "thunderstorm" ? 0.2 + Math.random() * 0.35 : 0.15 + Math.random() * 0.25,
        wind: type === "drizzle" ? 0.5 : type === "thunderstorm" ? 2.5 + Math.random() * 3 : 1.5 + Math.random() * 1.5,
      }));
    } else {
      s.raindrops = [];
    }
    s.splashes = [];

    // Snow
    if (type === "snow") {
      s.snowflakes = Array.from({ length: 120 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        speed: 0.5 + Math.random() * 1.5,
        radius: 1.5 + Math.random() * 3,
        opacity: 0.3 + Math.random() * 0.5,
        drift: (Math.random() - 0.5) * 0.8,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: 0.01 + Math.random() * 0.03,
      }));
    } else {
      s.snowflakes = [];
    }

    // Fog
    if (type === "fog") {
      s.fogPatches = Array.from({ length: 8 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        radius: 200 + Math.random() * 400,
        opacity: 0.03 + Math.random() * 0.06,
        speed: 0.15 + Math.random() * 0.3,
      }));
    } else {
      s.fogPatches = [];
    }

    s.lightning = { opacity: 0, timer: 0, active: false, nextFlash: 60 + Math.random() * 150, bolts: [] };

    // Clouds — more realistic multi-blob shapes
    const needsClouds = type === "cloudy" || type === "partly-cloudy" || type === "rain" || type === "heavy-rain" || type === "thunderstorm" || type === "drizzle";
    if (needsClouds) {
      const cloudCount = type === "partly-cloudy" ? 4 : type === "cloudy" ? 7 : 5;
      s.clouds = Array.from({ length: cloudCount }, () => {
        const scale = 0.7 + Math.random() * 0.8;
        const c = makeCloud(
          Math.random() * (w + 400) - 200,
          Math.random() * h * 0.35 + 20,
          scale
        );
        if (type !== "partly-cloudy") c.opacity *= 1.3;
        return c;
      });
    } else {
      s.clouds = [];
    }

    // Stars for night/dawn/dusk
    if (tod === "night" || tod === "dawn" || tod === "dusk") {
      const starCount = tod === "night" ? 80 : 30;
      s.stars = Array.from({ length: starCount }, () => ({
        x: Math.random() * w,
        y: Math.random() * h * 0.6,
        r: 0.5 + Math.random() * 1.2,
        twinkleSpeed: 0.01 + Math.random() * 0.04,
        phase: Math.random() * Math.PI * 2,
      }));
    } else {
      s.stars = [];
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    const type = getWeatherType(weatherCode);
    const tod = getTimeOfDay(sunrise, sunset, timezone);
    let w = window.innerWidth;
    let h = window.innerHeight;

    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w;
      canvas.height = h;
      initParticles(type, tod, w, h);
    };
    resize();

    let rectTimer = 0;

    const draw = () => {
      const s = stateRef.current;
      s.time++;
      rectTimer++;

      if (rectTimer % 30 === 0) {
        s.cardRects = getCardRects();
      }

      // Smoothly lerp current gradient toward target
      const gl = 0.01;
      s.currentGradient.top = lerpColor(s.currentGradient.top, s.targetGradient.top, gl);
      s.currentGradient.mid = lerpColor(s.currentGradient.mid, s.targetGradient.mid, gl);
      s.currentGradient.bot = lerpColor(s.currentGradient.bot, s.targetGradient.bot, gl);

      // Background gradient
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, s.currentGradient.top);
      grad.addColorStop(0.5, s.currentGradient.mid);
      grad.addColorStop(1, s.currentGradient.bot);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Stars
      if (s.stars.length > 0) {
        const starAlpha = s.timeOfDay === "night" ? 1 : 0.4;
        // Don't show stars in overcast/rainy/snowy
        const weatherDim = (s.type === "cloudy" || s.type === "rain" || s.type === "heavy-rain" || s.type === "thunderstorm" || s.type === "snow" || s.type === "fog") ? 0.15 : 1;
        for (const star of s.stars) {
          const twinkle = 0.4 + 0.6 * Math.sin(s.time * star.twinkleSpeed + star.phase);
          ctx.beginPath();
          ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${twinkle * starAlpha * weatherDim * 0.7})`;
          ctx.fill();
        }
      }

      // Sun / Moon glow
      const showSun = (s.timeOfDay === "day" || s.timeOfDay === "dawn" || s.timeOfDay === "dusk") &&
        (s.type === "clear" || s.type === "partly-cloudy");
      const showMoon = s.timeOfDay === "night" && (s.type === "clear" || s.type === "partly-cloudy");

      if (showSun) {
        // Sun position: dawn = low left, day = top center, dusk = low right
        let sunX = w * 0.5;
        let sunY = h * 0.08;
        if (s.timeOfDay === "dawn") { sunX = w * 0.25; sunY = h * 0.18; }
        if (s.timeOfDay === "dusk") { sunX = w * 0.75; sunY = h * 0.18; }

        const pulse = Math.sin(s.time * 0.008) * 0.15 + 1;

        // Warm atmospheric scattering glow
        const scatter = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 500 * pulse);
        scatter.addColorStop(0, "rgba(253, 224, 71, 0.07)");
        scatter.addColorStop(0.2, "rgba(251, 191, 36, 0.04)");
        scatter.addColorStop(0.5, "rgba(249, 115, 22, 0.02)");
        scatter.addColorStop(1, "rgba(249, 115, 22, 0)");
        ctx.fillStyle = scatter;
        ctx.fillRect(0, 0, w, h);

        // Main sun body — layered glows
        for (let i = 3; i >= 0; i--) {
          const r = (20 + i * 30) * pulse;
          const alpha = [0.12, 0.06, 0.03, 0.015][i];
          const g = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, r);
          g.addColorStop(0, `rgba(253, 224, 71, ${alpha})`);
          g.addColorStop(0.6, `rgba(251, 191, 36, ${alpha * 0.4})`);
          g.addColorStop(1, "rgba(251, 191, 36, 0)");
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(sunX, sunY, r, 0, Math.PI * 2);
          ctx.fill();
        }

        // Bright core
        const coreGrad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 12);
        coreGrad.addColorStop(0, "rgba(255, 255, 240, 0.25)");
        coreGrad.addColorStop(1, "rgba(253, 224, 71, 0)");
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.arc(sunX, sunY, 12, 0, Math.PI * 2);
        ctx.fill();

        // Subtle lens flare streaks
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        for (let i = 0; i < 4; i++) {
          const angle = (i / 4) * Math.PI + s.time * 0.001;
          const len = 60 + 30 * Math.sin(s.time * 0.01 + i * 2);
          const x1 = sunX + Math.cos(angle) * len;
          const y1 = sunY + Math.sin(angle) * len;
          const x2 = sunX - Math.cos(angle) * len;
          const y2 = sunY - Math.sin(angle) * len;
          const flare = ctx.createLinearGradient(x2, y2, x1, y1);
          flare.addColorStop(0, "rgba(253, 224, 71, 0)");
          flare.addColorStop(0.5, "rgba(253, 224, 71, 0.015)");
          flare.addColorStop(1, "rgba(253, 224, 71, 0)");
          ctx.strokeStyle = "rgba(253, 224, 71, 0.02)";
          ctx.lineWidth = 8 + 4 * Math.sin(s.time * 0.015 + i);
          ctx.beginPath();
          ctx.moveTo(x2, y2);
          ctx.lineTo(x1, y1);
          ctx.stroke();
        }
        ctx.restore();
      }

      if (showMoon) {
        const moonX = w * 0.8;
        const moonY = h * 0.1;

        // Moonlight glow
        const moonGlow = ctx.createRadialGradient(moonX, moonY, 0, moonX, moonY, 200);
        moonGlow.addColorStop(0, "rgba(226, 232, 240, 0.05)");
        moonGlow.addColorStop(0.4, "rgba(186, 230, 253, 0.02)");
        moonGlow.addColorStop(1, "rgba(186, 230, 253, 0)");
        ctx.fillStyle = moonGlow;
        ctx.fillRect(0, 0, w, h);

        // Moon disc
        ctx.beginPath();
        ctx.arc(moonX, moonY, 18, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(226, 232, 240, 0.12)";
        ctx.fill();

        // Crescent shadow
        ctx.beginPath();
        ctx.arc(moonX + 7, moonY - 3, 15, 0, Math.PI * 2);
        ctx.fillStyle = s.currentGradient.top;
        ctx.fill();
      }

      // Dawn/dusk horizon glow
      if (s.timeOfDay === "dawn" || s.timeOfDay === "dusk") {
        const horizonGrad = ctx.createLinearGradient(0, h * 0.6, 0, h);
        const hueColor = s.timeOfDay === "dawn" ? "251, 146, 60" : "239, 68, 68";
        horizonGrad.addColorStop(0, "rgba(" + hueColor + ", 0)");
        horizonGrad.addColorStop(0.5, "rgba(" + hueColor + ", 0.04)");
        horizonGrad.addColorStop(1, "rgba(" + hueColor + ", 0.08)");
        ctx.fillStyle = horizonGrad;
        ctx.fillRect(0, h * 0.6, w, h * 0.4);
      }

      // Clouds — multi-blob realistic
      for (const cloud of s.clouds) {
        cloud.x += cloud.speed;
        if (cloud.x > w + cloud.width * 1.5) cloud.x = -cloud.width * 1.5;

        ctx.save();
        ctx.translate(cloud.x, cloud.y);

        // Cloud shadow
        ctx.save();
        ctx.translate(4, 6);
        for (const blob of cloud.blobs) {
          ctx.beginPath();
          ctx.ellipse(blob.cx, blob.cy, blob.rx, blob.ry, 0, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0, 0, 0, ${cloud.opacity * 0.3})`;
          ctx.fill();
        }
        ctx.restore();

        // Main cloud body — bottom layer (darker)
        for (const blob of cloud.blobs) {
          ctx.beginPath();
          ctx.ellipse(blob.cx, blob.cy, blob.rx, blob.ry, 0, 0, Math.PI * 2);
          const isDay = s.timeOfDay === "day";
          const isDawn = s.timeOfDay === "dawn" || s.timeOfDay === "dusk";
          let fillR = 148, fillG = 163, fillB = 184; // slate-400
          if (s.type === "thunderstorm" || s.type === "heavy-rain") { fillR = 71; fillG = 85; fillB = 105; }
          else if (s.type === "rain" || s.type === "drizzle") { fillR = 100; fillG = 116; fillB = 139; }
          else if (isDay) { fillR = 203; fillG = 213; fillB = 225; }
          else if (isDawn) { fillR = 180; fillG = 160; fillB = 150; }
          ctx.fillStyle = `rgba(${fillR}, ${fillG}, ${fillB}, ${cloud.opacity})`;
          ctx.fill();
        }

        // Highlight on top blobs (lighter)
        for (let i = 1; i < cloud.blobs.length; i++) {
          const blob = cloud.blobs[i];
          const highlightGrad = ctx.createRadialGradient(
            blob.cx, blob.cy - blob.ry * 0.3, 0,
            blob.cx, blob.cy, blob.ry
          );
          const isDay = s.timeOfDay === "day";
          const highAlpha = isDay ? cloud.opacity * 0.6 : cloud.opacity * 0.3;
          highlightGrad.addColorStop(0, `rgba(255, 255, 255, ${highAlpha})`);
          highlightGrad.addColorStop(1, "rgba(255, 255, 255, 0)");
          ctx.beginPath();
          ctx.ellipse(blob.cx, blob.cy, blob.rx, blob.ry, 0, 0, Math.PI * 2);
          ctx.fillStyle = highlightGrad;
          ctx.fill();
        }

        ctx.restore();
      }

      // Fog
      for (const fog of s.fogPatches) {
        fog.x += fog.speed;
        if (fog.x > w + fog.radius) fog.x = -fog.radius;

        const wobbleY = Math.sin(s.time * 0.005 + fog.x * 0.001) * 20;
        const g = ctx.createRadialGradient(fog.x, fog.y + wobbleY, 0, fog.x, fog.y + wobbleY, fog.radius);
        g.addColorStop(0, `rgba(148, 163, 184, ${fog.opacity})`);
        g.addColorStop(1, "rgba(148, 163, 184, 0)");
        ctx.fillStyle = g;
        ctx.fillRect(fog.x - fog.radius, fog.y + wobbleY - fog.radius, fog.radius * 2, fog.radius * 2);
      }

      // Rain
      if (s.raindrops.length > 0) {
        ctx.lineCap = "round";
        for (const drop of s.raindrops) {
          drop.x += drop.wind;
          drop.y += drop.speed;

          let hitSurface = false;
          for (const rect of s.cardRects) {
            const scrollY = window.scrollY;
            const top = rect.top + scrollY;
            if (
              drop.x >= rect.left - 2 &&
              drop.x <= rect.right + 2 &&
              drop.y >= top - 2 &&
              drop.y <= top + 6
            ) {
              hitSurface = true;
              s.splashes.push({
                x: drop.x, y: top, radius: 0,
                maxRadius: 3 + Math.random() * 5,
                opacity: 0.4 + Math.random() * 0.3, life: 1,
              });
              break;
            }
          }

          if (hitSurface || drop.y > h + 10) {
            if (!hitSurface && drop.y > h) {
              s.splashes.push({
                x: drop.x, y: h, radius: 0,
                maxRadius: 2 + Math.random() * 4,
                opacity: 0.3, life: 1,
              });
            }
            drop.y = -Math.random() * h * 0.5 - 20;
            drop.x = Math.random() * (w + 200) - 100;
          }

          ctx.beginPath();
          ctx.moveTo(drop.x, drop.y);
          ctx.lineTo(drop.x - drop.wind * 0.5, drop.y - drop.length);
          ctx.strokeStyle = `rgba(147, 197, 253, ${drop.opacity})`;
          ctx.lineWidth = s.type === "thunderstorm" ? 1.8 : 1.2;
          ctx.stroke();
        }

        // Splashes
        for (let i = s.splashes.length - 1; i >= 0; i--) {
          const sp = s.splashes[i];
          sp.radius += 0.4;
          sp.life -= 0.035;
          sp.opacity *= 0.96;

          if (sp.life <= 0 || sp.radius > sp.maxRadius) {
            s.splashes.splice(i, 1);
            continue;
          }

          ctx.beginPath();
          ctx.ellipse(sp.x, sp.y, sp.radius, sp.radius * 0.3, 0, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(147, 197, 253, ${sp.opacity * sp.life})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();

          for (let d = 0; d < 2; d++) {
            const angle = (d / 2) * Math.PI - Math.PI * 0.1 + Math.random() * 0.2;
            const dist = sp.radius * 0.8;
            const dx = sp.x + Math.cos(angle) * dist;
            const dy = sp.y - Math.sin(angle) * dist * 0.6;
            ctx.beginPath();
            ctx.arc(dx, dy, 0.6, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(147, 197, 253, ${sp.opacity * sp.life * 0.6})`;
            ctx.fill();
          }
        }
      }

      // Snow
      for (const flake of s.snowflakes) {
        flake.wobble += flake.wobbleSpeed;
        flake.x += flake.drift + Math.sin(flake.wobble) * 0.5;
        flake.y += flake.speed;

        if (flake.y > h + 10) { flake.y = -10; flake.x = Math.random() * w; }
        if (flake.x > w + 10) flake.x = -10;
        if (flake.x < -10) flake.x = w + 10;

        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(226, 232, 240, ${flake.opacity})`;
        ctx.fill();
      }

      // Lightning
      if (s.type === "thunderstorm") {
        s.lightning.timer++;
        if (s.lightning.timer >= s.lightning.nextFlash && !s.lightning.active) {
          s.lightning.active = true;
          s.lightning.opacity = 0.5 + Math.random() * 0.4;
          s.lightning.timer = 0;

          // Generate lightning bolt(s)
          const boltCount = Math.random() > 0.5 ? 2 : 1;
          s.lightning.bolts = [];
          for (let b = 0; b < boltCount; b++) {
            const startX = w * (0.15 + Math.random() * 0.7);
            const segments: { x: number; y: number }[] = [{ x: startX, y: 0 }];
            let cx = startX;
            let cy = 0;
            const targetY = h * (0.4 + Math.random() * 0.5);
            const steps = 12 + Math.floor(Math.random() * 10);

            for (let i = 1; i <= steps; i++) {
              const t = i / steps;
              cy = targetY * t;
              cx += (Math.random() - 0.5) * 80;
              segments.push({ x: cx, y: cy });
            }

            // Branches
            const branches: { x: number; y: number }[][] = [];
            for (let i = 2; i < segments.length - 1; i++) {
              if (Math.random() > 0.55) {
                const branch: { x: number; y: number }[] = [{ x: segments[i].x, y: segments[i].y }];
                let bx = segments[i].x;
                let by = segments[i].y;
                const dir = Math.random() > 0.5 ? 1 : -1;
                const branchSteps = 3 + Math.floor(Math.random() * 5);
                for (let j = 0; j < branchSteps; j++) {
                  bx += dir * (15 + Math.random() * 25);
                  by += 15 + Math.random() * 20;
                  branch.push({ x: bx, y: by });
                }
                branches.push(branch);
              }
            }

            s.lightning.bolts.push({
              segments,
              opacity: 0.8 + Math.random() * 0.2,
              width: 2 + Math.random() * 2,
              branches,
            });
          }
        }

        if (s.lightning.active) {
          // Sky flash
          ctx.fillStyle = `rgba(199, 210, 254, ${s.lightning.opacity * 0.4})`;
          ctx.fillRect(0, 0, w, h);

          // Draw bolts
          for (const bolt of s.lightning.bolts) {
            const boltAlpha = s.lightning.opacity * bolt.opacity;

            // Outer glow
            ctx.save();
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.shadowColor = `rgba(167, 180, 255, ${boltAlpha})`;
            ctx.shadowBlur = 30;

            ctx.beginPath();
            ctx.moveTo(bolt.segments[0].x, bolt.segments[0].y);
            for (let i = 1; i < bolt.segments.length; i++) {
              ctx.lineTo(bolt.segments[i].x, bolt.segments[i].y);
            }
            ctx.strokeStyle = `rgba(200, 210, 255, ${boltAlpha * 0.4})`;
            ctx.lineWidth = bolt.width + 8;
            ctx.stroke();

            // Mid glow
            ctx.beginPath();
            ctx.moveTo(bolt.segments[0].x, bolt.segments[0].y);
            for (let i = 1; i < bolt.segments.length; i++) {
              ctx.lineTo(bolt.segments[i].x, bolt.segments[i].y);
            }
            ctx.strokeStyle = `rgba(220, 225, 255, ${boltAlpha * 0.7})`;
            ctx.lineWidth = bolt.width + 3;
            ctx.stroke();

            // Core
            ctx.shadowBlur = 0;
            ctx.beginPath();
            ctx.moveTo(bolt.segments[0].x, bolt.segments[0].y);
            for (let i = 1; i < bolt.segments.length; i++) {
              ctx.lineTo(bolt.segments[i].x, bolt.segments[i].y);
            }
            ctx.strokeStyle = `rgba(255, 255, 255, ${boltAlpha})`;
            ctx.lineWidth = bolt.width;
            ctx.stroke();

            // Branches
            for (const branch of bolt.branches) {
              ctx.beginPath();
              ctx.moveTo(branch[0].x, branch[0].y);
              for (let i = 1; i < branch.length; i++) {
                ctx.lineTo(branch[i].x, branch[i].y);
              }
              ctx.strokeStyle = `rgba(200, 210, 255, ${boltAlpha * 0.5})`;
              ctx.lineWidth = bolt.width * 0.5 + 2;
              ctx.stroke();

              ctx.beginPath();
              ctx.moveTo(branch[0].x, branch[0].y);
              for (let i = 1; i < branch.length; i++) {
                ctx.lineTo(branch[i].x, branch[i].y);
              }
              ctx.strokeStyle = `rgba(255, 255, 255, ${boltAlpha * 0.6})`;
              ctx.lineWidth = bolt.width * 0.4;
              ctx.stroke();
            }

            ctx.restore();
          }

          s.lightning.opacity *= 0.88;

          if (s.lightning.opacity < 0.01) {
            s.lightning.active = false;
            s.lightning.bolts = [];
            s.lightning.nextFlash = 40 + Math.random() * 200;
            // Chance of rapid follow-up flash
            if (Math.random() > 0.5) {
              s.lightning.nextFlash = 4 + Math.random() * 8;
            }
          }
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [weatherCode, sunrise, sunset, timezone, initParticles, getCardRects]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full"
      style={{ zIndex: 0 }}
    />
  );
}
